import React from 'react'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import { observer } from 'mobx-react-lite'

import { Icon } from '@mxenabled/mx-icons'
import { H3, Text } from '@mxenabled/mxui'

import {
  TipSection,
  type TipSectionProps,
} from '../../../insights/components/shared/ChartDrawerTemplate/TipSection'
import { Loader, useGlobalCopyStore, useGlobalUiStore, useTransactionStore } from '../../../common'
import type { Transaction } from '../../../core'
import { exportTransactionsToCSV } from '../../../core/utils/TransactionUtils'
import { TransactionList } from '../../../transactions'

interface TabItem {
  label: string
  ariaLabel?: string
}

const PANEL_ID = 'transaction-drill-down-panel'
const tabId = (index: number) => `transaction-details-tab-${index}`

interface TransactionDrillDownProps {
  ariaLabel?: string
  heading?: string
  subHeadingText?: string
  icon?: React.ReactNode
  tabs?: TabItem[]
  selectedIndex: number
  top: React.ReactNode
  tipSection?: TipSectionProps | false
  onSelectionChange?: (selectedIndex: number) => void
  onEachRowClick?: (guid: string) => void
  transactionFilter?: (selectedIndex: number) => (transaction: Transaction) => boolean
  baseTransactions?: Transaction[]
}

export const TransactionDrillDown = observer(function TransactionDrillDown({
  ariaLabel,
  heading,
  subHeadingText,
  icon,
  tabs,
  selectedIndex,
  top,
  onSelectionChange,
  transactionFilter,
  tipSection,
  onEachRowClick,
  baseTransactions,
}: TransactionDrillDownProps) {
  const { transactions: transactionsCopy } = useGlobalCopyStore()
  const { isCopyLoaded } = useGlobalUiStore()
  const { sortedTransactionsWithSplits, tags, detailedTransactions } = useTransactionStore()

  if (!isCopyLoaded) return <Loader />

  const hasTabs = !!tabs?.length
  const tablistLabel = ariaLabel ?? transactionsCopy.transactions_title

  const handlePrimaryChange = (_event: React.SyntheticEvent, newValue: number) => {
    onSelectionChange?.(newValue)
  }

  const filteredTransactions = transactionFilter
    ? (baseTransactions ?? detailedTransactions).filter(transactionFilter(selectedIndex))
    : undefined

  const exportTransactions = filteredTransactions ?? sortedTransactionsWithSplits

  return (
    <Box sx={{ bgcolor: 'background.paper', width: '100%' }}>
      {hasTabs && (
        <Tabs
          aria-label={tablistLabel}
          onChange={handlePrimaryChange}
          value={selectedIndex}
          variant="fullWidth"
        >
          {tabs!.map((tab, index) => (
            <Tab
              aria-controls={PANEL_ID}
              aria-label={tab.ariaLabel}
              id={tabId(index)}
              key={`${tab.label}-${index}`}
              label={tab.label}
              sx={{ minWidth: 'auto', px: 0 }}
            />
          ))}
        </Tabs>
      )}

      <Box
        aria-labelledby={hasTabs ? tabId(selectedIndex) : undefined}
        id={PANEL_ID}
        role={hasTabs ? 'tabpanel' : undefined}
      >
        {(heading || subHeadingText) && (
          <Stack sx={{ gap: 4, pt: 16, px: 24 }}>
            {heading && <H3>{heading}</H3>}
            {subHeadingText && (
              <Stack sx={{ alignItems: 'center', flexDirection: 'row', gap: 8 }}>
                {icon}
                <Text textTransform="uppercase" variant="body2">
                  {subHeadingText}
                </Text>
              </Stack>
            )}
          </Stack>
        )}

        <Box sx={{ pt: 16 }}>{top}</Box>

        {/* TODO: need to use a nseperate compnent from insight-v2, for now using insights-shared component which will be shifted under insights-v2 in future */}
        {tipSection && <TipSection {...tipSection} />}

        <Stack
          sx={{
            alignItems: 'center',
            borderBottomColor: 'divider',
            borderBottomStyle: 'solid',
            borderBottomWidth: 1,
            flexDirection: 'row',
            justifyContent: 'space-between',
            pb: 4,
            pt: 16,
            px: 24,
          }}
        >
          <Text bold={true} variant="body1">
            {transactionsCopy.transactions_title}
          </Text>
          {exportTransactions.length > 0 && (
            <Button
              onClick={() => {
                exportTransactionsToCSV(exportTransactions, tags)
              }}
              startIcon={<Icon name="ios_share" />}
              sx={{ height: 28 }}
            >
              <Text bold={true} variant="body2">
                {transactionsCopy.export_csv_btn}
              </Text>
            </Button>
          )}
        </Stack>

        <TransactionList transaction={filteredTransactions} onClick={onEachRowClick} />
      </Box>
    </Box>
  )
})

export default TransactionDrillDown
